import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { derivePipeline, urgency, SALES_OUTCOMES } from '../lib/pipeline'
import ServiceSwitch from '../components/ServiceSwitch'
import IATracker from '../components/IATracker'
import { failed } from '../lib/toast'

// The salesperson's view of where each client is. One stage per client, in order.
const SALES_STAGES = ['Not started', 'Form received', 'Quoted', 'Signed', 'Scheduled', 'Audit done']
const STAGE_COLOR = {
  'Not started': ['var(--bg2)', 'var(--text2)'],
  'Form received': ['#FFF3E0', '#854F0B'],         // the client replied — a quote is owed
  Quoted: ['var(--accent-bg)', 'var(--accent-text)'],
  Signed: ['var(--accent-bg)', 'var(--accent-text)'],
  Scheduled: ['var(--accent-bg)', 'var(--accent-text)'],
  'Audit done': ['#EAF3DE', '#27500a'],
}

// Map a pipeline row (lib/pipeline.js) to { key, detail } — or null to leave it off the list.
//   • "Awaiting quote" and "Awaiting renewal" both mean the renewal has not started — unless the client has already
//     sent the form (a Draft quote → "Form received").
//   • "Awaiting renewal" with the next expiry more than 6 months away = just renewed: off the list until the next
//     renewal window (it returns as "Not started").
//   • A certified client keeps its last completed job until a new job has an audit date, so a NEWER quote (made after
//     the last audit) decides the stage of the new cycle.
function salesStage(r) {
  const days = r.daysToExpiry
  const newCycleQuote = r.stage === 'Awaiting renewal' && r.quoteCreatedAt && r.lastAuditDate && new Date(r.quoteCreatedAt) > new Date(r.lastAuditDate)
  if (r.stage === 'Awaiting renewal' && !newCycleQuote) {
    // Just renewed (next expiry more than 6 months away) — nothing for sales to do: off the list until the next
    // renewal window, when it returns as "Not started".
    if (days != null && days > 180) return null
    return { key: 'Not started' }
  }
  if (r.stage === 'Awaiting quote' || newCycleQuote) {
    if (r.quoteStatus === 'Draft') return { key: 'Form received' }
    if (newCycleQuote && r.quoteStatus === 'Sent') return { key: 'Quoted', detail: r.quoteSentDate ? `sent ${fmtDate(r.quoteSentDate)}` : '' }
    if (newCycleQuote && r.quoteStatus === 'Signed') return { key: 'Signed', detail: r.signedDate ? fmtDate(r.signedDate) : '' }
    return { key: 'Not started' }
  }
  if (r.stage === 'Quoted') return { key: 'Quoted', detail: r.quoteSentDate ? `sent ${fmtDate(r.quoteSentDate)}` : '' }
  if (r.stage === 'Signed') return { key: 'Signed', detail: r.signedDate ? fmtDate(r.signedDate) : '' }
  if (r.stage === 'Scheduled') return { key: 'Scheduled', detail: r.auditDate ? fmtDate(r.auditDate) : '' }
  if (r.stage === 'Audit done') return { key: 'Audit done', detail: r.postAudit === 'Report received' ? 'report received' : 'awaiting report' }
  return { key: 'Not started' }
}

function monthLabel(mk) {
  if (!mk) return 'Unscheduled'
  const [y, m] = mk.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
}

// Format a YYYY-MM-DD expiry as "30 Aug 2026" (parsed locally to avoid timezone day-shift).
function fmtDate(iso) {
  if (!iso) return ''
  const [y, m, d] = String(iso).split('-').map(Number)
  if (!y || !m || !d) return ''
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const urgColor = {
  overdue: ['#FDECEA', '#791F1F'], red: ['#FDECEA', '#791F1F'],
  amber: ['#FFF3E0', '#854F0B'], ok: ['var(--bg2)', 'var(--text2)'], none: ['var(--bg2)', 'var(--text3)'],
}

export default function Pipeline() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [staff, setStaff] = useState([])  // user_profiles names for the owner picker
  const [stageFilter, setStageFilter] = useState('')   // '' = all stages
  const [ownerFilter, setOwnerFilter] = useState('')   // '' = all owners · '__none' = unassigned
  const [search, setSearch] = useState('')
  const [service, setService] = useState('biz')

  useEffect(() => { load() }, [])

  async function load() {
    const [{ data: clients }, { data: quotations }, { data: jobs }, { data: reminders }, { data: meta }, { data: profiles }] = await Promise.all([
      supabase.from('clients').select('id,name,uen,bizsafe_expiry_date,contact_name,contact_email,industry'),
      supabase.from('quotations').select('id,quote_ref,status,sent_date,signed_date,prospect_name,client_id,audit_type,prepared_by,created_at,form_responses'),
      supabase.from('jobs').select('id,client_id,quote_ref,audit_date,report_submitted,status,audit_type,scope,combined_group_id,is_combined_primary'),
      supabase.from('renewal_reminders_sent').select('client_id,created_at'),
      supabase.from('pipeline_meta').select('*'),
      supabase.from('user_profiles').select('name').order('name'),
    ])
    setStaff((profiles || []).map(p => p.name).filter(Boolean))
    setRows(derivePipeline({ clients: clients || [], quotations: quotations || [], jobs: jobs || [], reminders: reminders || [], meta: meta || [] }))
    setLoading(false)
  }

  async function setMeta(key, patch) {
    const now = new Date().toISOString()
    const payload = { key, ...patch, updated_at: now, updated_by: profile?.name || 'Staff' }
    if ('sales_outcome' in patch) { payload.sales_outcome_at = now; payload.sales_outcome_by = profile?.name || 'Staff' }
    if ('owner' in patch) { payload.owner_set_at = now; payload.owner_set_by = profile?.name || 'Staff' }
    const { error } = await supabase.from('pipeline_meta').upsert(payload, { onConflict: 'key' })
    if (error) { failed("Couldn't save", error.message); return }
    load()
  }

  // [bizSAFE | Internal Audit] — Internal Audit data loads only when its switch is clicked (IATracker mounts then).
  const header = (
    <div className="page-header">
      <h1>Tracker</h1>
      <ServiceSwitch value={service} onChange={setService} />
    </div>
  )
  if (service === 'ia') return <div>{header}<IATracker /></div>

  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>

  // ONE list: every existing client in its renewal window + newly signed clients, each at ONE sales stage
  // (salesStage). Just-renewed clients are left off until they're within 6 months of the next expiry (they come back
  // as "Not started"). Display only — derivePipeline (shared with the Dashboard)
  // is unchanged.
  const staged = rows.map(r => ({ ...r, ss: salesStage(r) })).filter(r => r.ss)
  const owners = [...new Set(staged.map(r => r.owner).filter(Boolean))].sort()
  let base = staged
  if (ownerFilter === '__none') base = base.filter(r => !r.owner)
  else if (ownerFilter) base = base.filter(r => r.owner === ownerFilter)
  if (search) base = base.filter(r => (r.client || '').toLowerCase().includes(search.toLowerCase()))
  const counts = Object.fromEntries(SALES_STAGES.map(s => [s, base.filter(r => r.ss.key === s).length]))
  const visible = stageFilter ? base.filter(r => r.ss.key === stageFilter) : base

  // Group by month
  const groups = {}
  for (const r of visible) { const k = r.monthGroup || 'zzz'; (groups[k] = groups[k] || []).push(r) }
  const monthKeys = Object.keys(groups).sort()

  const GRID = '1.6fr 0.9fr 1fr 1.4fr 1fr 24px'
  const pill = on => ({ fontSize: 13, padding: '4px 10px', borderRadius: 14, cursor: 'pointer', whiteSpace: 'nowrap',
    border: `0.5px solid ${on ? 'var(--brand)' : 'var(--border2)'}`, background: on ? 'var(--brand)' : 'var(--bg)', color: on ? '#fff' : 'var(--text)' })

  return (
    <div>
      {header}

      {/* Stage summary = the filter (click again for all) · owner · search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <button type="button" style={pill(!stageFilter)} onClick={() => setStageFilter('')}>All {base.length}</button>
        {SALES_STAGES.map(s => (
          <button key={s} type="button" style={pill(stageFilter === s)} onClick={() => setStageFilter(stageFilter === s ? '' : s)}>{s} {counts[s]}</button>
        ))}
        <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)} style={{ marginLeft: 'auto', width: 'auto', fontSize: 13 }}>
          <option value="">All owners</option>
          {owners.map(o => <option key={o} value={o}>{o}</option>)}
          <option value="__none">Unassigned</option>
        </select>
        <input placeholder="Search client…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 180 }} />
      </div>

      {visible.length === 0 && <div className="card" style={{ color: 'var(--text3)', fontSize: 13 }}>No clients here.</div>}

      {monthKeys.map(mk => (
        <div key={mk} className="card" style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
            <strong style={{ fontSize: 13 }}>{monthLabel(mk === 'zzz' ? null : mk)}</strong>
            <span style={{ fontSize: 13, color: 'var(--text3)' }}>{groups[mk].length} item{groups[mk].length > 1 ? 's' : ''}</span>
          </div>
          {/* pipe-scroll: on phones this becomes a horizontal swipe region (see App.css) so the
              9-column grid keeps readable column widths instead of crushing/overlapping. */}
          <div className="pipe-scroll">
          <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '6px 14px', fontSize: 13, color: 'var(--text3)', borderBottom: '1px solid var(--border)' }}>
            <div>Client</div><div>Owner</div><div>Expiry</div><div>Stage</div><div>Outcome</div><div />
          </div>
          {groups[mk].sort((a, b) => (a.daysToExpiry ?? 9999) - (b.daysToExpiry ?? 9999)).map(r => {
            const u = urgency(r)
            const [ubg, ufg] = urgColor[u.tier] || urgColor.none
            const [sbg, sfg] = STAGE_COLOR[r.ss.key] || STAGE_COLOR['Not started']
            const open = ['Not started', 'Form received', 'Quoted'].includes(r.ss.key)   // outcome only before signing
            return (
              <div key={r.key} style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '8px 14px', borderBottom: '1px solid var(--border)', alignItems: 'center', fontSize: 13 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.client}>{r.client}</div>
                  <div style={{ fontSize: 13, color: 'var(--text3)' }}>{r.type} · {r.service}</div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <select value={r.owner || ''} onChange={e => setMeta(r.key, { owner: e.target.value || null })} style={{ fontSize: 13, padding: '3px 6px', width: '100%' }}
                    title={r.owner && r.ownerSetBy ? `Owner set by ${r.ownerSetBy}${r.ownerSetAt ? ` on ${new Date(r.ownerSetAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}` : undefined}>
                    <option value="">— unassigned —</option>
                    {(r.owner && !staff.includes(r.owner)) && <option value={r.owner}>{r.owner}</option>}
                    {staff.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {!r.owner && r.suggestedOwner && (
                    <button className="sm" onClick={() => setMeta(r.key, { owner: r.suggestedOwner })}
                      title={`Suggested from the quote preparer`} style={{ fontSize: 13, padding: '1px 6px', marginTop: 3 }}>
                      Assign {r.suggestedOwner}
                    </button>
                  )}
                </div>
                {/* Expiry + how long left / overdue */}
                <div style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                  {(r.type === 'Renewal' && r.expiry) ? <>
                    <span style={{ color: (u.tier === 'overdue' || u.tier === 'red') ? '#791F1F' : 'var(--text)' }}>{fmtDate(r.expiry)}</span>
                    {u.label && <span style={{ marginLeft: 6, fontSize: 13, padding: '1px 7px', borderRadius: 12, background: ubg, color: ufg }}>{u.tier === 'overdue' ? u.label : `${u.label} left`}</span>}
                  </> : <span style={{ color: 'var(--text3)' }}>—</span>}
                </div>
                {/* Stage — one chip + its detail (date / report status) */}
                <div style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13, padding: '2px 8px', borderRadius: 12, background: sbg, color: sfg, whiteSpace: 'nowrap' }}>{r.ss.key}</span>
                  {r.ss.detail && <span style={{ marginLeft: 6, fontSize: 13, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{r.ss.detail}</span>}
                </div>
                <div>
                  {open && (r.outcomeAuto
                    ? <span title="Declined quote">{r.salesOutcome}</span>
                    : <select value={r.salesOutcome || 'Open'} onChange={e => setMeta(r.key, { sales_outcome: e.target.value })} style={{ fontSize: 13, padding: '3px 6px', width: '100%' }}
                        title="On hold / No response / Rejected / Closed down stops chasing this client">
                        {SALES_OUTCOMES.map(o => <option key={o} value={o}>{o === 'Open' ? 'Open' : o}</option>)}
                      </select>)}
                </div>
                <button className="sm" title="Open" onClick={() => r.jobId ? navigate(`/jobs/${r.jobId}`) : navigate('/clients')} style={{ padding: '2px 6px', fontSize: 13 }}>→</button>
              </div>
            )
          })}
          </div>
        </div>
      ))}
    </div>
  )
}
